import { Box, Button, InputAdornment } from '@mui/material';
import { useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import FormErrorMessage from './FormErrorMessage/FormErrorMessage';
import LimitField from './LimitField/LimitField';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import useMobile from 'src/shared/hooks/useMobile';
import { TextField } from 'mui-rff';
import { FormContentSection } from './FormContentSection';
import { type FlexflowLimits } from 'src/modules/Flexflow/Contracts/types';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const FOOD_LIMIT = 'foodLimit';
const TRANSPORT_LIMIT = 'transportLimit';

export const FOOD_MAX_LIMIT_AMOUNT = 220;
export const TRANSPORT_MAX_LIMIT_AMOUNT = 136.36;

type FormContentProps = {
  currencySymbol: string;
  monthlyTotalLimit: number;
  submit: () => void;
  employeeLimits: FlexflowLimits;
};

const FormContent = ({ currencySymbol, monthlyTotalLimit, employeeLimits, submit }: FormContentProps) => {
  const classes = useStyles();
  const isMobile = useMobile();
  const { t } = useTranslation(['flexflow', 'form']);
  const { valid, values, pristine } = useFormState();
  const totalLimit = Number(Number(monthlyTotalLimit).toFixed(2));

  const { effectiveFoodLimit, effectiveTransportLimit, globalLimit } = employeeLimits || {};

  const inputAdornmentProps = {
    startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
    endAdornment: (
      <InputAdornment position="end">
        <LockOutlinedIcon fontSize="small" sx={{ opacity: 0.4 }} />
      </InputAdornment>
    )
  };

  const currentLimitData = [
    {
      label: t('employee.total limit'),
      name: 'currentTotal',
      value: globalLimit
    },
    {
      label: t('employee.food limit'),
      name: 'currentFood',
      value: effectiveFoodLimit
    },
    {
      label: t('employee.transport limit'),
      name: 'currentTransport',
      value: effectiveTransportLimit
    }
  ];

  return (
    <Grid2 xs={12} sx={{ w: '100%', maxWidth: '600px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormContentSection
          isMobile={isMobile}
          title={t('employee.effective limits')}
          description={t('employee.effective limits description')}
        >
          {currentLimitData.map(e => (
            <TextField disabled InputProps={inputAdornmentProps} key={e.name} placeholder="-" {...e} />
          ))}
        </FormContentSection>
        <FormContentSection
          isMobile={isMobile}
          title={t('employee.edit limit title')}
          description={t('employee.edit limit description')}
        >
          <TextField
            disabled
            label={t('employee.total limit')}
            name="futureTotalLimit"
            value={totalLimit}
            InputProps={inputAdornmentProps}
          />
          <LimitField
            fieldName={FOOD_LIMIT}
            fieldMaxLimit={FOOD_MAX_LIMIT_AMOUNT}
            totalLimit={totalLimit}
            currencySymbol={currencySymbol}
            inputLabel={t('employee.food limit') ?? ''}
          />
          <LimitField
            fieldName={TRANSPORT_LIMIT}
            fieldMaxLimit={TRANSPORT_MAX_LIMIT_AMOUNT}
            totalLimit={totalLimit}
            currencySymbol={currencySymbol}
            inputLabel={t('employee.transport limit') ?? ''}
          />
        </FormContentSection>
      </Box>
      {totalLimit < Number(values.foodLimit ?? 0) + Number(values.transportLimit ?? 0) && (
        <FormErrorMessage totalLimit={totalLimit} />
      )}
      <Grid2 xs={12} className={classes.buttonWrapper} sx={{ mt: '1rem' }}>
        <Button disabled={!valid || pristine} onClick={submit} variant="contained" color="primary" type="submit">
          {t('common:save')}
        </Button>
      </Grid2>
    </Grid2>
  );
};

export default FormContent;
